import React, { useState, useEffect } from 'react'
import { Modal, Button, Form, InputGroup, Badge, Spinner } from 'react-bootstrap'
import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import { toast } from 'react-toastify'
import { GET_ALL_RECIPIENTS } from '../../../gql/queries/contactsApiQueries'
import {
  ADD_CAMPAIGN_TARGETS_BY_FILTER,
  REMOVE_CAMPAIGN_TARGETS_BY_FILTER,
} from '../../../gql/mutations/campaigns'
import { GET_ALL_CARRIERS_LIST } from '../../../gql/queries/carriersQuery'
import { GET_ALL_STATES_EU_LIST } from '../../../gql/queries/statesEUQuery'
import { GET_CONTACT_TAGS } from '../../../gql/queries/tags'
import { GET_ALL_POLICIES_STATUSES, GET_ALL_POLICIES_TYPES } from '../../../gql/queries/policiesQueries'
import { GET_CAMPAIGN_TARGET_CONTACT_IDS } from '../../../gql/queries/campaigns'
import { IResRecipientsAPI, NodeContact } from '../../../types/contactsApi'
import { PhoneProviderActions } from '../../../components/telephony/PhoneProviderActions'
import { getTagContrastColor, parseId } from '../../../helpers'
import { MessageDescriptor, useIntl, FormattedMessage } from 'react-intl'
import {
  RECIPIENT_MESSAGES as R,
  POLICY_FILTER_MESSAGES as P,
} from '../../recipients/recipientMessages'
import { CAMPAIGN_MESSAGES as M, CSV_LABEL, ELLIPSIS, fullName } from '../campaignMessages'
import { CSVImportModal } from './CSVImportModal'
import { RecipientFilterModal, RecipientFilters, EMPTY_RECIPIENT_FILTERS } from './RecipientFilterModal'
import './RecipientSelectorModal.css'

const normalizeContactId = (id?: string | null) => {
  if (!id) return ''
  if (/^\d+$/.test(id)) return id
  try {
    const decoded = atob(id)
    const parts = decoded.split(':')
    const lastPart = parts[parts.length - 1]
    return /^\d+$/.test(lastPart) ? lastPart : id
  } catch {
    return id
  }
}

const uniqueContactIds = (ids: string[]) => {
  const seen = new Set<string>()
  return ids.reduce<string[]>((result, id) => {
    const normalized = normalizeContactId(id)
    if (!normalized || seen.has(normalized)) return result
    seen.add(normalized)
    result.push(normalized)
    return result
  }, [])
}

interface RecipientSelectorModalProps {
  show: boolean
  onHide: () => void
  initialSelectedIds: string[]
  initialTargetCount?: number
  onSave: (selectedContactIds: string[]) => void
  confirmLabel?: string
  campaignId?: string
  onBulkAdded?: () => void
}

export const RecipientSelectorModal: React.FC<RecipientSelectorModalProps> = ({
  show,
  onHide,
  initialSelectedIds,
  initialTargetCount = 0,
  onSave,
  confirmLabel,
  campaignId,
  onBulkAdded,
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const intl = useIntl()
  const [selectedIds, setSelectedIds] = useState<string[]>(initialSelectedIds.map(normalizeContactId))
  const [idsLoaded, setIdsLoaded] = useState(initialSelectedIds.length > 0)
  const [highlightedAvailable, setHighlightedAvailable] = useState<string[]>([])
  const [highlightedSelected, setHighlightedSelected] = useState<string[]>([])
  const [showCSVImport, setShowCSVImport] = useState(false)
  const [showFilterModal, setShowFilterModal] = useState(false)
  const [showAvailableList, setShowAvailableList] = useState(false)
  const [showSelectedList, setShowSelectedList] = useState(false)
  const [activeFilters, setActiveFilters] = useState<RecipientFilters>(EMPTY_RECIPIENT_FILTERS)
  const [knownContacts, setKnownContacts] = useState<Record<string, NodeContact>>({})

  // Derived filter strings for queries
  const activeStatesValue = activeFilters.state.length > 0 ? activeFilters.state.join(',') : undefined
  const activeCarriersValue = activeFilters.carrier.length > 0 ? activeFilters.carrier.join(',') : undefined
  const activePolicyStatusValue = activeFilters.policyStatus.length > 0 ? activeFilters.policyStatus.join(',') : undefined
  const activePolicyTypeValue = activeFilters.policyType.length > 0 ? activeFilters.policyType.join(',') : undefined
  const activeTagsValue = activeFilters.tags.length > 0 ? activeFilters.tags.join(',') : undefined
  const activeEffectiveYearValue = activeFilters.effectiveYears.length > 0 ? activeFilters.effectiveYears.join(',') : undefined
  const activeUserValue = activeFilters.user.length > 0 ? activeFilters.user.join(',') : undefined
  const activeAssignedToValue = activeFilters.assignedTo.length > 0 ? activeFilters.assignedTo.join(',') : undefined
  const activeImmigrationStatusValue = activeFilters.immigrationStatus.length > 0 ? activeFilters.immigrationStatus.join(',') : undefined
  const activeDocumentStatusValue = activeFilters.documentStatus.length > 0 ? activeFilters.documentStatus.join(',') : undefined
  const activePaymentStatusValue = activeFilters.paymentStatus.length > 0 ? activeFilters.paymentStatus.join(',') : undefined
  const activeLeadSourceValue = activeFilters.leadSource.length > 0 ? activeFilters.leadSource.join(',') : undefined
  const activeCarrierStatusValue = activeFilters.carrierStatusGeneric.length > 0 ? activeFilters.carrierStatusGeneric.join(',') : undefined

  const activePolicyRelationship =
    activeFilters.withoutPolicy === true
      ? 'no_policy'
      : activeFilters.isPolicyHolder === true
        ? 'holder'
        : activeFilters.isPolicyHolder === false && activeFilters.withoutPolicy === false
          ? 'family'
          : ''

  const activeFilterCount = [
    activeFilters.state.length > 0,
    activeFilters.carrier.length > 0,
    activeFilters.policyStatus.length > 0,
    activeFilters.policyType.length > 0,
    !!activePolicyRelationship,
    activeFilters.effectiveYears.length > 0,
    activeFilters.tags.length > 0,
    !!(activeFilters.policyCreatedFrom || activeFilters.policyCreatedTo),
    !!(activeFilters.submissionDateFrom || activeFilters.submissionDateTo),
    activeFilters.user.length > 0,
    activeFilters.assignedTo.length > 0,
    !!activeFilters.zipCode,
    activeFilters.immigrationStatus.length > 0,
    !!activeFilters.holderGender,
    !!activeFilters.stateJurisdictionType,
    activeFilters.paymentStatus.length > 0,
    activeFilters.documentStatus.length > 0,
    !!activeFilters.metalLevel,
    activeFilters.leadSource.length > 0,
    activeFilters.hasMemberId !== undefined,
    activeFilters.commissionPayable !== undefined,
    !!activeFilters.commissionPaymentStatus,
    activeFilters.unassigned !== undefined,
    activeFilters.ownsCar !== undefined,
    activeFilters.ownsHome !== undefined,
    activeFilters.hasAutoPolicy !== undefined,
    activeFilters.hasHomePolicy !== undefined,
    activeFilters.carrierStatusGeneric.length > 0,
    !!(activeFilters.carrierLastAsOfDateAfter || activeFilters.carrierLastAsOfDateBefore),
    activeFilters.carrierIsAgeIn !== undefined,
    activeFilters.carrierIsDelinquent !== undefined,
    activeFilters.carrierIsUnpaidPending !== undefined,
    activeFilters.carrierIsUnpaidPastDue !== undefined,
  ].filter(Boolean).length

  // Lookup data for filters and chips
  const { data: carriersData } = useQuery(GET_ALL_CARRIERS_LIST, { skip: !show })
  const { data: statesData } = useQuery(GET_ALL_STATES_EU_LIST, { skip: !show })
  const { data: tagsData } = useQuery(GET_CONTACT_TAGS, { skip: !show })
  const { data: policyStatusesData } = useQuery(GET_ALL_POLICIES_STATUSES, { skip: !show })
  const { data: policyTypesData } = useQuery(GET_ALL_POLICIES_TYPES, { skip: !show, variables: { first: 100 } })

  const carriers: { id: string; name: string }[] = carriersData?.allInsuranceOperatorsList ?? []
  const states: { id: string; name: string; code: string }[] = statesData?.allStatesList ?? []
  const tags: { id: string; name: string; color: string }[] = tagsData?.contactTags ?? []

  // Label maps for chip display
  const policyStatusLabelMap: Record<string, string> = Object.fromEntries(
    (policyStatusesData?.allPolicyStatuses?.edges ?? []).map((e: any) => [e.node.code, e.node.title])
  )
  const policyTypeLabelMap: Record<string, string> = Object.fromEntries(
    (policyTypesData?.allPoliciesType?.edges ?? []).map((e: any) => [String(parseId(e.node.id)), e.node.typePolicy])
  )

  const [getRecipients, { data, loading, error }] = useLazyQuery<IResRecipientsAPI>(
    GET_ALL_RECIPIENTS,
    { fetchPolicy: 'network-only' }
  )

  const [getContactsById, { data: dataById }] = useLazyQuery<IResRecipientsAPI>(
    GET_ALL_RECIPIENTS,
    { fetchPolicy: 'network-only' }
  )

  const [getSelectedRecipients, { data: selectedData, loading: loadingSelected }] = useLazyQuery<IResRecipientsAPI>(
    GET_ALL_RECIPIENTS,
    { fetchPolicy: 'network-only' }
  )

  const [getAllMatchingIds, { loading: loadingRemoveAll }] = useLazyQuery<IResRecipientsAPI>(
    GET_ALL_RECIPIENTS,
    { fetchPolicy: 'network-only' }
  )

  const [addByFilter, { loading: addingByFilter }] = useMutation(ADD_CAMPAIGN_TARGETS_BY_FILTER)
  const [removeByFilter, { loading: removingByFilter }] = useMutation(REMOVE_CAMPAIGN_TARGETS_BY_FILTER)

  const [fetchCampaignTargetIds, { loading: loadingCampaignIds }] = useLazyQuery(
    GET_CAMPAIGN_TARGET_CONTACT_IDS,
    { fetchPolicy: 'no-cache' }
  )

  // Update known contacts cache
  useEffect(() => {
    if (data?.allRecipients?.edges) {
      setKnownContacts((prev) => {
        const next = { ...prev }
        data.allRecipients.edges.forEach((edge) => { next[normalizeContactId(edge.node.id)] = edge.node })
        return next
      })
    }
  }, [data])

  useEffect(() => {
    if (dataById?.allRecipients?.edges) {
      setKnownContacts((prev) => {
        const next = { ...prev }
        dataById.allRecipients.edges.forEach((edge) => { next[normalizeContactId(edge.node.id)] = edge.node })
        return next
      })
    }
  }, [dataById])

  useEffect(() => {
    if (selectedData?.allRecipients?.edges) {
      setKnownContacts((prev) => {
        const next = { ...prev }
        selectedData.allRecipients.edges.forEach((edge) => { next[normalizeContactId(edge.node.id)] = edge.node })
        return next
      })
    }
  }, [selectedData])

  // Fetch initial selected contacts if missing from cache
  useEffect(() => {
    if (show && initialSelectedIds.length > 0) {
      const normalizedInitialIds = initialSelectedIds.map(normalizeContactId)
      const missingIds = normalizedInitialIds.filter((id) => !knownContacts[id])
      if (missingIds.length > 0) {
        getContactsById({ variables: { ids: missingIds.join(','), first: missingIds.length } })
      }
    }
  }, [show, initialSelectedIds, knownContacts, getContactsById])

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300)
    return () => clearTimeout(timer)
  }, [searchTerm])

  // Fetch contacts when search, filters, or list visibility change.
  // first: 0 when collapsed → only totalCount returned (fast).
  // first: 100 when expanded → full contact list.
  useEffect(() => {
    if (show) {
      getRecipients({
        variables: {
          name: debouncedSearch || undefined,
          state: activeStatesValue,
          carrier: activeCarriersValue,
          policyStatus: activePolicyStatusValue,
          policyType: activePolicyTypeValue,
          isPolicyHolder: activeFilters.isPolicyHolder,
          withoutPolicy: activeFilters.withoutPolicy,
          effectiveYear: activeEffectiveYearValue,
          tags: activeTagsValue,
          policyCreatedFrom: activeFilters.policyCreatedFrom || undefined,
          policyCreatedTo: activeFilters.policyCreatedTo || undefined,
          submissionDateFrom: activeFilters.submissionDateFrom || undefined,
          submissionDateTo: activeFilters.submissionDateTo || undefined,
          user: activeUserValue,
          assignedTo: activeAssignedToValue,
          zipCode: activeFilters.zipCode || undefined,
          immigrationStatus: activeImmigrationStatusValue,
          gender: activeFilters.holderGender || undefined,
          stateJurisdictionType: activeFilters.stateJurisdictionType || undefined,
          paymentStatus: activePaymentStatusValue,
          documentStatus: activeDocumentStatusValue,
          metalLevel: activeFilters.metalLevel || undefined,
          leadSource: activeLeadSourceValue,
          hasMemberId: activeFilters.hasMemberId,
          commissionPayable: activeFilters.commissionPayable,
          commissionPaymentStatus: activeFilters.commissionPaymentStatus || undefined,
          unassigned: activeFilters.unassigned,
          ownsCar: activeFilters.ownsCar,
          ownsHome: activeFilters.ownsHome,
          hasAutoPolicy: activeFilters.hasAutoPolicy,
          hasHomePolicy: activeFilters.hasHomePolicy,
          carrierStatusGeneric: activeCarrierStatusValue,
          carrierLastAsOfDateAfter: activeFilters.carrierLastAsOfDateAfter || undefined,
          carrierLastAsOfDateBefore: activeFilters.carrierLastAsOfDateBefore || undefined,
          carrierIsAgeIn: activeFilters.carrierIsAgeIn,
          carrierIsDelinquent: activeFilters.carrierIsDelinquent,
          carrierIsUnpaidPending: activeFilters.carrierIsUnpaidPending,
          carrierIsUnpaidPastDue: activeFilters.carrierIsUnpaidPastDue,
          first: showAvailableList ? 100 : 0,
          offset: 0,
        },
      })
    }
  }, [debouncedSearch, activeFilters, activeStatesValue, activeCarriersValue, activePolicyStatusValue, activePolicyTypeValue, activeTagsValue, activeEffectiveYearValue, activeUserValue, activeAssignedToValue, activeImmigrationStatusValue, activeDocumentStatusValue, activePaymentStatusValue, activeLeadSourceValue, activeCarrierStatusValue, show, showAvailableList, getRecipients])

  useEffect(() => {
    if (!show || !idsLoaded || !showSelectedList || selectedIds.length === 0) return
    const hasSearchOrFilters = !!(debouncedSearch || activeFilterCount > 0)
    if (!hasSearchOrFilters) return

    getSelectedRecipients({
      variables: {
        ids: selectedIds.join(','),
        name: debouncedSearch || undefined,
        state: activeStatesValue,
        carrier: activeCarriersValue,
        policyStatus: activePolicyStatusValue,
        policyType: activePolicyTypeValue,
        isPolicyHolder: activeFilters.isPolicyHolder,
        withoutPolicy: activeFilters.withoutPolicy,
        effectiveYear: activeEffectiveYearValue,
        tags: activeTagsValue,
        policyCreatedFrom: activeFilters.policyCreatedFrom || undefined,
        policyCreatedTo: activeFilters.policyCreatedTo || undefined,
        submissionDateFrom: activeFilters.submissionDateFrom || undefined,
        submissionDateTo: activeFilters.submissionDateTo || undefined,
        user: activeUserValue,
        assignedTo: activeAssignedToValue,
        zipCode: activeFilters.zipCode || undefined,
        immigrationStatus: activeImmigrationStatusValue,
        gender: activeFilters.holderGender || undefined,
        stateJurisdictionType: activeFilters.stateJurisdictionType || undefined,
        paymentStatus: activePaymentStatusValue,
        documentStatus: activeDocumentStatusValue,
        metalLevel: activeFilters.metalLevel || undefined,
        leadSource: activeLeadSourceValue,
        hasMemberId: activeFilters.hasMemberId,
        commissionPayable: activeFilters.commissionPayable,
        commissionPaymentStatus: activeFilters.commissionPaymentStatus || undefined,
        unassigned: activeFilters.unassigned,
        ownsCar: activeFilters.ownsCar,
        ownsHome: activeFilters.ownsHome,
        hasAutoPolicy: activeFilters.hasAutoPolicy,
        hasHomePolicy: activeFilters.hasHomePolicy,
        carrierStatusGeneric: activeCarrierStatusValue,
        carrierLastAsOfDateAfter: activeFilters.carrierLastAsOfDateAfter || undefined,
        carrierLastAsOfDateBefore: activeFilters.carrierLastAsOfDateBefore || undefined,
        carrierIsAgeIn: activeFilters.carrierIsAgeIn,
        carrierIsDelinquent: activeFilters.carrierIsDelinquent,
        carrierIsUnpaidPending: activeFilters.carrierIsUnpaidPending,
        carrierIsUnpaidPastDue: activeFilters.carrierIsUnpaidPastDue,
        first: selectedIds.length,
        offset: 0,
      },
    })
  }, [selectedIds, idsLoaded, showSelectedList, debouncedSearch, activeFilters, activeFilterCount, activeStatesValue, activeCarriersValue, activePolicyStatusValue, activePolicyTypeValue, activeTagsValue, activeEffectiveYearValue, activeUserValue, activeAssignedToValue, activeImmigrationStatusValue, activeDocumentStatusValue, activePaymentStatusValue, activeLeadSourceValue, activeCarrierStatusValue, show, getSelectedRecipients])

  // Reset on open
  useEffect(() => {
    if (show) {
      const normalized = initialSelectedIds.map(normalizeContactId)
      setSelectedIds(normalized)
      setIdsLoaded(normalized.length > 0)
      setHighlightedAvailable([])
      setHighlightedSelected([])
      setSearchTerm('')
      setShowCSVImport(false)
      setShowFilterModal(false)
      setShowAvailableList(false)
      setShowSelectedList(false)
      setActiveFilters(EMPTY_RECIPIENT_FILTERS)
    }
  }, [show, initialSelectedIds])

  const allRecipients = data?.allRecipients?.edges ?? []
  const totalCount: number = (data?.allRecipients as any)?.totalCount ?? 0
  const selectedIdSet = new Set(selectedIds)
  const availableContacts = allRecipients.filter((edge) => !selectedIdSet.has(normalizeContactId(edge.node.id)))

  const hasSearchOrFilters = !!(debouncedSearch || activeFilterCount > 0)

  const matchingSelectedIds = hasSearchOrFilters
    ? new Set((selectedData?.allRecipients?.edges ?? []).map((edge) => normalizeContactId(edge.node.id)))
    : null
  const visibleSelectedIds = hasSearchOrFilters
    ? selectedIds.filter((id) => matchingSelectedIds?.has(id))
    : selectedIds
  const selectedContacts = visibleSelectedIds.map((id) => knownContacts[id]).filter(Boolean)

  // Move handlers
  const moveToSelected = () => {
    setSelectedIds((prev) => uniqueContactIds([...prev, ...highlightedAvailable]))
    setHighlightedAvailable([])
  }
  const moveToAvailable = () => {
    setSelectedIds(selectedIds.filter((id) => !highlightedSelected.includes(id)))
    setHighlightedSelected([])
  }
  const moveAllToSelected = () => {
    const allIds = availableContacts.map((edge) => normalizeContactId(edge.node.id))
    setSelectedIds((prev) => uniqueContactIds([...prev, ...allIds]))
  }
  const moveAllToAvailable = () => {
    setSelectedIds([])
    setHighlightedSelected([])
  }

  const toggleAvailableHighlight = (id: string) => {
    setHighlightedAvailable((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    )
  }
  const toggleSelectedHighlight = (id: string) => {
    setHighlightedSelected((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    )
  }

  // Bulk mutation variables — all filters now supported by the mutation
  const mutationFilterVars = {
    name: debouncedSearch || undefined,
    state: activeStatesValue,
    carrier: activeCarriersValue,
    policyStatus: activePolicyStatusValue,
    policyType: activePolicyTypeValue,
    effectiveYear: activeEffectiveYearValue,
    tags: activeTagsValue,
    isPolicyHolder: activeFilters.isPolicyHolder,
    withoutPolicy: activeFilters.withoutPolicy,
    policyCreatedFrom: activeFilters.policyCreatedFrom || undefined,
    policyCreatedTo: activeFilters.policyCreatedTo || undefined,
    submissionDateFrom: activeFilters.submissionDateFrom || undefined,
    submissionDateTo: activeFilters.submissionDateTo || undefined,
    user: activeUserValue,
    assignedTo: activeAssignedToValue,
    zipCode: activeFilters.zipCode || undefined,
    immigrationStatus: activeImmigrationStatusValue,
    gender: activeFilters.holderGender || undefined,
    stateJurisdictionType: activeFilters.stateJurisdictionType || undefined,
    paymentStatus: activePaymentStatusValue,
    documentStatus: activeDocumentStatusValue,
    metalLevel: activeFilters.metalLevel || undefined,
    leadSource: activeLeadSourceValue,
    hasMemberId: activeFilters.hasMemberId,
    commissionPayable: activeFilters.commissionPayable,
    commissionPaymentStatus: activeFilters.commissionPaymentStatus || undefined,
    unassigned: activeFilters.unassigned,
    ownsCar: activeFilters.ownsCar,
    ownsHome: activeFilters.ownsHome,
    hasAutoPolicy: activeFilters.hasAutoPolicy,
    hasHomePolicy: activeFilters.hasHomePolicy,
    carrierStatusGeneric: activeCarrierStatusValue,
    carrierLastAsOfDateAfter: activeFilters.carrierLastAsOfDateAfter || undefined,
    carrierLastAsOfDateBefore: activeFilters.carrierLastAsOfDateBefore || undefined,
    carrierIsAgeIn: activeFilters.carrierIsAgeIn,
    carrierIsDelinquent: activeFilters.carrierIsDelinquent,
    carrierIsUnpaidPending: activeFilters.carrierIsUnpaidPending,
    carrierIsUnpaidPastDue: activeFilters.carrierIsUnpaidPastDue,
  }

  const handleAddAllMatching = async () => {
    if (!campaignId) return
    try {
      const { data: result } = await addByFilter({
        variables: { campaignId, ...mutationFilterVars },
      })
      const added = result?.addCampaignTargetsByFilter?.addedCount ?? 0
      const skipped = result?.addCampaignTargetsByFilter?.skippedCount ?? 0
      const addedMsg = intl.formatMessage(M.toastAddedAsTargets, { count: added })
      toast.success(
        skipped > 0
          ? `${addedMsg} ${intl.formatMessage(M.toastAlreadyExisted, { count: skipped })}`
          : addedMsg
      )
      onBulkAdded?.()
      onHide()
    } catch {
      toast.error(intl.formatMessage(M.toastAddAllError))
    }
  }

  const handleRemoveAllMatching = async () => {
    if (campaignId) {
      try {
        const { data: result } = await removeByFilter({
          variables: { campaignId, ...mutationFilterVars },
        })
        const removed = result?.removeCampaignTargetsByFilter?.removedCount ?? 0
        toast.success(intl.formatMessage(M.toastTargetsRemoved, { count: removed }))
        onBulkAdded?.()
        onHide()
        return
      } catch {
        toast.error(intl.formatMessage(M.toastRemoveAllError))
        return
      }
    }

    if (!hasSearchOrFilters) {
      setSelectedIds([])
      setHighlightedSelected([])
      return
    }

    const PAGE_SIZE = 100
    let offset = 0
    const allMatchingIds: string[] = []

    while (true) {
      const { data: result } = await getAllMatchingIds({
        variables: {
          ...mutationFilterVars,
          first: PAGE_SIZE,
          offset,
        },
      })
      const edges = result?.allRecipients?.edges ?? []
      edges.forEach((e: any) => allMatchingIds.push(normalizeContactId(e.node.id)))
      if (edges.length < PAGE_SIZE) break
      offset += PAGE_SIZE
    }

    const matchingIds = new Set(allMatchingIds)
    setSelectedIds((prev) => prev.filter((id) => !matchingIds.has(id)))
    setHighlightedSelected([])
  }

  /** `Carrier: Aetna, Cigna` — la etiqueta sale de la misma llave que usa el modal (§17.13). */
  const chip = (label: MessageDescriptor, value: string) =>
    intl.formatMessage(R.chipValue, { label: intl.formatMessage(label), value })
  /** `2026-01-01 – 2026-03-31`, con puntos suspensivos cuando falta un extremo. */
  const chipRange = (from?: string, to?: string) =>
    `${from || ELLIPSIS} – ${to || ELLIPSIS}`

  const handleSave = () => onSave(selectedIds)

  const handleImportSuccess = (importedContactIds: string[]) => {
    setSelectedIds((prev) => uniqueContactIds([...prev, ...importedContactIds]))
    getRecipients({ variables: { name: debouncedSearch || undefined, first: 100, offset: 0 } })
  }

  const removeChipFilter = (patch: Partial<RecipientFilters>) =>
    setActiveFilters((f) => ({ ...f, ...patch }))

  const renderContactItem = (
    contact: NodeContact,
    isHighlighted: boolean,
    onToggle: () => void
  ) => (
    <div
      key={contact.id}
      className={`recipient-item ${isHighlighted ? 'highlighted' : ''}`}
      onClick={onToggle}
    >
      <div className='form-check'>
        <input
          className='form-check-input'
          type='checkbox'
          checked={isHighlighted}
          onChange={onToggle}
          onClick={(e) => e.stopPropagation()}
        />
        <div className='recipient-info'>
          <div className='fw-bold'>
            {contact.fullName || fullName(contact.firstName, contact.lastName)}
          </div>
          <div className='text-muted small'>
            {contact.email && <span className='me-3'>{contact.email}</span>}
            {contact.phone && (
              <PhoneProviderActions
                phone={contact.phone}
                stopPropagation={false}
                className='d-inline-flex flex-column align-items-start'
                textClassName='text-muted small'
              />
            )}
          </div>
          {contact.tagAssignments && contact.tagAssignments.length > 0 && (
            <div className='d-flex flex-wrap gap-1 mt-1'>
              {contact.tagAssignments.map((a: any) => (
                <span
                  key={a.tag.id}
                  className='badge rounded-pill'
                  style={{
                    backgroundColor: a.tag.color,
                    color: getTagContrastColor(a.tag.color),
                    fontSize: '0.65rem',
                  }}
                >
                  {a.tag.name}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )

  return (
    <Modal show={show} onHide={onHide} size='xl' centered>
      <Modal.Header closeButton>
        <Modal.Title><FormattedMessage {...M.selectorTitle} /></Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* ── Search + toolbar ── */}
        <div className='selection-toolbar mb-3'>
          <div className='selection-toolbar-main'>
            <InputGroup className='selection-search'>
              <Form.Control
                type='text'
                placeholder={intl.formatMessage(M.selectorSearch)}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {(loading || loadingSelected) && (
                <InputGroup.Text>
                  <Spinner animation='border' size='sm' />
                </InputGroup.Text>
              )}
            </InputGroup>
            <div className='selection-toolbar-actions'>
              <Button
                size='sm'
                variant={activeFilterCount > 0 ? 'secondary' : 'light'}
                onClick={() => setShowFilterModal(true)}
              >
                <i className='bi bi-funnel me-1'></i>
                <FormattedMessage {...R.filtersTitle} />
                {activeFilterCount > 0 && (
                  <Badge bg='light' text='primary' pill className='ms-1'>{activeFilterCount}</Badge>
                )}
              </Button>
              <Button
                variant='outline-success'
                size='sm'
                onClick={() => setShowCSVImport(true)}
                title={intl.formatMessage(M.importCsv)}
              >
                <i className='bi bi-file-earmark-arrow-up me-1'></i>
                {CSV_LABEL}
              </Button>
            </div>
          </div>

          {/* ── Active filter chips ── */}
          {activeFilterCount > 0 && (
            <div className='d-flex flex-wrap gap-1 mt-2'>
              {activeFilters.state.length > 0 && (
                <span className='badge fw-normal d-inline-flex align-items-center gap-1' style={{ backgroundColor: '#0d6efd', color: '#fff' }}>
                  {chip(P.state, activeFilters.state.map((c) => states.find((s) => s.code === c)?.name ?? c).join(', '))}
                  <i className='bi bi-x' style={{ cursor: 'pointer' }} onClick={() => removeChipFilter({ state: [] })} />
                </span>
              )}
              {activeFilters.carrier.length > 0 && (
                <span className='badge fw-normal d-inline-flex align-items-center gap-1' style={{ backgroundColor: '#0d6efd', color: '#fff' }}>
                  {chip(P.carrier, activeFilters.carrier.map((id) => carriers.find((c) => c.id === id)?.name ?? id).join(', '))}
                  <i className='bi bi-x' style={{ cursor: 'pointer' }} onClick={() => removeChipFilter({ carrier: [] })} />
                </span>
              )}
              {activeFilters.policyStatus.length > 0 && (
                <span className='badge fw-normal d-inline-flex align-items-center gap-1' style={{ backgroundColor: '#0d6efd', color: '#fff' }}>
                  {chip(P.policyStatus, activeFilters.policyStatus.map((code) => policyStatusLabelMap[code] ?? code).join(', '))}
                  <i className='bi bi-x' style={{ cursor: 'pointer' }} onClick={() => removeChipFilter({ policyStatus: [] })} />
                </span>
              )}
              {activeFilters.policyType.length > 0 && (
                <span className='badge fw-normal d-inline-flex align-items-center gap-1' style={{ backgroundColor: '#6f42c1', color: '#fff' }}>
                  {chip(P.policyTypes, activeFilters.policyType.map((id) => policyTypeLabelMap[id] ?? id).join(', '))}
                  <i className='bi bi-x' style={{ cursor: 'pointer' }} onClick={() => removeChipFilter({ policyType: [] })} />
                </span>
              )}
              {activePolicyRelationship && (
                <span className='badge fw-normal d-inline-flex align-items-center gap-1' style={{ backgroundColor: '#0d6efd', color: '#fff' }}>
                  {intl.formatMessage(
                    activePolicyRelationship === 'no_policy'
                      ? R.optNoPolicy
                      : activePolicyRelationship === 'holder'
                        ? R.optPolicyHolders
                        : R.optFamilyMembers
                  )}
                  <i className='bi bi-x' style={{ cursor: 'pointer' }} onClick={() => removeChipFilter({ isPolicyHolder: undefined, withoutPolicy: undefined })} />
                </span>
              )}
              {activeFilters.effectiveYears.length > 0 && (
                <span className='badge fw-normal d-inline-flex align-items-center gap-1' style={{ backgroundColor: '#6f42c1', color: '#fff' }}>
                  {chip(M.effectiveYear, activeFilters.effectiveYears.join(', '))}
                  <i className='bi bi-x' style={{ cursor: 'pointer' }} onClick={() => removeChipFilter({ effectiveYears: [] })} />
                </span>
              )}
              {activeFilters.tags.length > 0 && (() => {
                const firstTag = tags.find((t) => t.id === activeFilters.tags[0])
                const bg = firstTag?.color ?? '#6c757d'
                return (
                  <span className='badge fw-normal d-inline-flex align-items-center gap-1' style={{ backgroundColor: bg, color: getTagContrastColor(bg) }}>
                    {chip(R.tags, activeFilters.tags.map((id) => tags.find((t) => t.id === id)?.name ?? id).join(', '))}
                    <i className='bi bi-x' style={{ cursor: 'pointer' }} onClick={() => removeChipFilter({ tags: [] })} />
                  </span>
                )
              })()}
              {(activeFilters.policyCreatedFrom || activeFilters.policyCreatedTo) && (
                <span className='badge fw-normal d-inline-flex align-items-center gap-1' style={{ backgroundColor: '#198754', color: '#fff' }}>
                  {chip(P.createdDate, chipRange(activeFilters.policyCreatedFrom, activeFilters.policyCreatedTo))}
                  <i className='bi bi-x' style={{ cursor: 'pointer' }} onClick={() => removeChipFilter({ policyCreatedFrom: '', policyCreatedTo: '' })} />
                </span>
              )}
              {(activeFilters.submissionDateFrom || activeFilters.submissionDateTo) && (
                <span className='badge fw-normal d-inline-flex align-items-center gap-1' style={{ backgroundColor: '#198754', color: '#fff' }}>
                  {chip(R.submissionDate, chipRange(activeFilters.submissionDateFrom, activeFilters.submissionDateTo))}
                  <i className='bi bi-x' style={{ cursor: 'pointer' }} onClick={() => removeChipFilter({ submissionDateFrom: '', submissionDateTo: '' })} />
                </span>
              )}
            </div>
          )}
        </div>

        {/* ── Two-column selector ── */}
        <div className='recipient-selector-container'>
          {/* Available Column (left) */}
          <div className='recipient-column'>
            <div className='column-header d-flex justify-content-between align-items-center'>
              <h6 className='mb-0'>
                <FormattedMessage {...M.availableContacts} />
                {loading ? (
                  <Spinner animation='border' size='sm' className='ms-2' />
                ) : totalCount > 0 ? (
                  <Badge bg='secondary' pill className='ms-2'>{totalCount}</Badge>
                ) : null}
              </h6>
              <Button
                variant='link'
                size='sm'
                className='p-0 text-muted'
                onClick={() => setShowAvailableList((v) => !v)}
                title={intl.formatMessage(showAvailableList ? M.hideList : M.showList)}
              >
                <i className={`bi bi-chevron-${showAvailableList ? 'up' : 'down'}`}></i>
                <span className='ms-1 small'>{intl.formatMessage(showAvailableList ? M.hide : M.show)}</span>
              </Button>
            </div>
            {showAvailableList && (
              <div className='contacts-list'>
                {error ? (
                  <div className='text-center text-danger p-4 small'>
                    <i className='bi bi-exclamation-triangle me-2'></i>
                    {error.message}
                  </div>
                ) : loading && availableContacts.length === 0 ? (
                  <div className='text-center p-4'>
                    <Spinner animation='border' size='sm' />
                  </div>
                ) : availableContacts.length === 0 ? (
                  <div className='text-center text-muted p-4'>
                    <i className='bi bi-inbox fs-1 d-block mb-2'></i>
                    <FormattedMessage {...M.noContactsFound} />
                  </div>
                ) : (
                  availableContacts.map(({ node: contact }) =>
                    renderContactItem(
                      contact,
                      highlightedAvailable.includes(normalizeContactId(contact.id)),
                      () => toggleAvailableHighlight(normalizeContactId(contact.id))
                    )
                  )
                )}
              </div>
            )}
          </div>

          {/* Control Buttons */}
          <div className='control-buttons'>
            <Button
              variant='outline-primary'
              size='sm'
              onClick={moveToSelected}
              disabled={highlightedAvailable.length === 0}
              title={intl.formatMessage(M.addSelected)}
            >
              <i className='bi bi-chevron-right'></i>
            </Button>
            <Button
              variant='outline-primary'
              size='sm'
              onClick={moveAllToSelected}
              disabled={availableContacts.length === 0}
              title={intl.formatMessage(M.addAllVisible)}
            >
              <i className='bi bi-chevron-double-right'></i>
            </Button>
            <Button
              variant='outline-secondary'
              size='sm'
              onClick={moveToAvailable}
              disabled={highlightedSelected.length === 0}
              title={intl.formatMessage(M.removeSelected)}
            >
              <i className='bi bi-chevron-left'></i>
            </Button>
            <Button
              variant='outline-secondary'
              size='sm'
              onClick={moveAllToAvailable}
              disabled={(idsLoaded ? selectedIds.length : initialTargetCount) === 0}
              title={intl.formatMessage(M.removeAll)}
            >
              <i className='bi bi-chevron-double-left'></i>
            </Button>
          </div>

          {/* Selected Column (right) */}
          <div className='recipient-column'>
            <div className='column-header'>
              <div className='d-flex justify-content-between align-items-center mb-2'>
                <h6 className='mb-0'>
                  <FormattedMessage {...M.selectedRecipients} />
                  {loadingCampaignIds || loadingSelected ? (
                    <Spinner animation='border' size='sm' className='ms-2' />
                  ) : (idsLoaded ? selectedIds.length : initialTargetCount) > 0 ? (
                    <Badge bg='success' pill className='ms-2'>
                      {idsLoaded ? selectedIds.length : initialTargetCount}
                    </Badge>
                  ) : null}
                </h6>
                <Button
                  variant='link'
                  size='sm'
                  className='p-0 text-muted'
                  disabled={loadingCampaignIds}
                  onClick={async () => {
                    if (!idsLoaded && campaignId) {
                      const result = await fetchCampaignTargetIds({ variables: { campaignId } })
                      const ids: string[] = (result.data?.campaignTargets?.items ?? []).map(
                        (item: any) => normalizeContactId(item.contact.id)
                      )
                      setSelectedIds(ids)
                      setIdsLoaded(true)
                      setShowSelectedList(true)
                    } else {
                      setShowSelectedList((v) => !v)
                    }
                  }}
                  title={intl.formatMessage(showSelectedList ? M.hideList : M.showList)}
                >
                  {loadingCampaignIds ? (
                    <Spinner animation='border' size='sm' />
                  ) : (
                    <>
                      <i className={`bi bi-chevron-${showSelectedList ? 'up' : 'down'}`}></i>
                      <span className='ms-1 small'>{intl.formatMessage(showSelectedList ? M.hide : M.show)}</span>
                    </>
                  )}
                </Button>
              </div>
              {(idsLoaded ? selectedIds.length : initialTargetCount) > 0 && (
                <div className='alert alert-success mb-0 py-1 small'>
                  <i className='bi bi-check-circle me-2'></i>
                  <FormattedMessage
                    {...R.contactsSelected}
                    values={{
                      count: idsLoaded ? selectedIds.length : initialTargetCount,
                      b: (chunks: React.ReactNode) => <>{chunks}</>,
                    }}
                  />
                  {idsLoaded && hasSearchOrFilters && (
                    <span className='ms-2 text-muted'>
                      <FormattedMessage
                        {...M.matchingCurrentFilters}
                        values={{count: visibleSelectedIds.length}}
                      />
                    </span>
                  )}
                </div>
              )}
            </div>

            {showSelectedList && (
              <div className='contacts-list'>
                {loadingSelected && hasSearchOrFilters && selectedContacts.length === 0 ? (
                  <div className='text-center p-4'>
                    <Spinner animation='border' size='sm' />
                  </div>
                ) : selectedContacts.length === 0 ? (
                  <div className='text-center text-muted p-4'>
                    <i className='bi bi-person-plus fs-1 d-block mb-2'></i>
                    {selectedIds.length > 0 ? (
                      <span>
                        {hasSearchOrFilters
                          ? intl.formatMessage(M.noSelectedMatch)
                          : intl.formatMessage(M.recipientsSelected, {count: selectedIds.length})}
                        <br />
                        <small className='text-muted'>
                          {intl.formatMessage(hasSearchOrFilters ? M.adjustFilters : M.detailsLoading)}
                        </small>
                      </span>
                    ) : (
                      <span>
                        <FormattedMessage {...M.noRecipientsYet} />
                        <br />
                        <small><FormattedMessage {...M.selectFromLeft} /></small>
                      </span>
                    )}
                  </div>
                ) : (
                  selectedContacts.map((contact) =>
                    renderContactItem(
                      contact,
                      highlightedSelected.includes(normalizeContactId(contact.id)),
                      () => toggleSelectedHighlight(normalizeContactId(contact.id))
                    )
                  )
                )}
              </div>
            )}
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer className='justify-content-between'>
        <div className='d-flex gap-2'>
          {campaignId && (
            <Button
              variant='outline-primary'
              onClick={handleAddAllMatching}
              disabled={addingByFilter || totalCount === 0}
              title={intl.formatMessage(M.addAllMatchingHint)}
            >
              {addingByFilter ? <Spinner animation='border' size='sm' className='me-2' /> : <i className='bi bi-people-fill me-2'></i>}
              <FormattedMessage {...M.addAllMatching} />
              {totalCount > 0 && <Badge bg='primary' pill className='ms-2'>{totalCount}</Badge>}
            </Button>
          )}
          <Button
            variant='outline-danger'
            onClick={handleRemoveAllMatching}
            disabled={(loadingRemoveAll || removingByFilter) || (idsLoaded ? selectedIds.length : initialTargetCount) === 0}
            title={intl.formatMessage(M.removeAllMatchingHint)}
          >
            {(loadingRemoveAll || removingByFilter) ? <Spinner animation='border' size='sm' className='me-2' /> : <i className='bi bi-person-dash-fill me-2'></i>}
            <FormattedMessage {...M.removeAllMatching} />
          </Button>
        </div>

        <div className='d-flex gap-2 align-items-center'>
          {(idsLoaded ? selectedIds.length : initialTargetCount) > 0 && (
            <span className='text-muted small me-2'>
              <i className='bi bi-check-circle-fill text-success me-1'></i>
              <FormattedMessage
                {...R.contactsSelected}
                values={{
                  count: idsLoaded ? selectedIds.length : initialTargetCount,
                  b: (chunks: React.ReactNode) => <>{chunks}</>,
                }}
              />
            </span>
          )}
          <Button variant='secondary' onClick={onHide}>
            <FormattedMessage {...M.cancel} />
          </Button>
          <Button
            variant='success'
            size='lg'
            onClick={handleSave}
            disabled={!idsLoaded}
            title={!idsLoaded ? intl.formatMessage(M.saveNeedsIdsHint) : undefined}
          >
            <i className='bi bi-check-lg me-2'></i>
            {!idsLoaded
              ? intl.formatMessage(M.saveNeedsIds)
              : confirmLabel || intl.formatMessage(M.saveSelection, { count: selectedIds.length })}
          </Button>
        </div>
      </Modal.Footer>

      <RecipientFilterModal
        isOpen={showFilterModal}
        onClose={() => setShowFilterModal(false)}
        onApply={(filters) => setActiveFilters(filters)}
        initialFilters={activeFilters}
        states={states}
        carriers={carriers}
        tags={tags}
      />

      <CSVImportModal
        show={showCSVImport}
        onHide={() => setShowCSVImport(false)}
        onImportSuccess={handleImportSuccess}
      />
    </Modal>
  )
}
